import { inject, injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';
import { IOsiSampleDocumentsWidgetViewModel } from '@shared/types/mobx/widgets/OsiSampleDocuments';
import { Abonent } from '@shared/types/osi/abonents';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { notifications } from '@shared/utils/notifications';
import api from '@app/api';
import logger from 'js-logger';
import { getOsiAbonents } from '@shared/api/osi/abonents/get';
import i18next from 'i18next';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { MIME, downloadBase64 } from '@shared/utils/download';

@injectable()
export class OsiSampleDocumentsWidgetViewModel implements IOsiSampleDocumentsWidgetViewModel {
  isLoading = false;

  private _abonents: Abonent[] = [];

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
    this.loadAbonents();
  }

  get sortedAbonents() {
    const preparedAbonents = this._abonents.map((abonent) => {
      // Фаза 2: не мутируем flat. flatRaw — сырой номер (для имени файла), flat — отображаемое значение:
      // арендатор → flatDisplay (единый номер от backend), остальные — сырой flat (makeFlat здесь не применялся).
      const raw = abonent.flat;
      const display = abonent.areaTypeCode === AreaTypeCodes.ARENDATOR ? (abonent.flatDisplay ?? raw) : raw;
      return { ...abonent, flatRaw: raw, flat: display };
    });

    return preparedAbonents.sort((a, b) => flatComparator(a.flat, b.flat));
  }

  downloadDebtor = async (abonent: Abonent) => {
    try {
      this.isLoading = true;
      // @ts-expect-error api endpoint not typed
      const { docBase64 } = await api.getDebtorNotification(abonent.id);
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64.
      // Фаза 2: имя файла — по СЫРОМУ номеру помещения (flatRaw)
      downloadBase64(
        docBase64,
        MIME.DOCX,
        `Уведомление_должнику_${(abonent as any).flatRaw ?? abonent.flat}_${new Date()
          .toISOString()
          .substring(0, 10)}.docx`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  // mode определяет вариант расчёта долга на бэке: 0=Current, 1=Overdue (дефолт), 2=Collection
  downloadNotary = async (abonent: Abonent, mode?: number) => {
    try {
      this.isLoading = true;
      // @ts-expect-error api endpoint not typed
      const { docBase64 } = await api.getNotaryNotification(abonent.id, mode);
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64.
      // Фаза 2: имя файла — по СЫРОМУ номеру помещения (flatRaw)
      downloadBase64(
        docBase64,
        MIME.DOCX,
        `${i18next.t('debts:notaryDocs')}_${(abonent as any).flatRaw ?? abonent.flat}_${new Date()
          .toISOString()
          .substring(0, 10)}.docx`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  private loadAbonents = async () => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;
      this._abonents = await getOsiAbonents(this.osiModule.osiId);
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };
}
